import React, { FC, useMemo } from "react";

import {
	Badge,
	Grid,
	SectionCard,
	Spinner,
	SummaryList,
} from "@amphore-dev/amphore-lib";
import { format, getISOWeek } from "date-fns";
import { t } from "i18next";
import { Link } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import MembersService from "@/services/MembersService";

import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useProjectOverview } from "@/hooks/useOverview";
import { useUserSettings } from "@/hooks/useUserSettings";

import { Dot } from "@/components/atoms/Dot/Dot";
import { FeatureGate } from "@/components/atoms/FeatureGate/FeatureGate";
import { MemberAvatar } from "@/components/molecules/MemberAvatar/MemberAvatar";
import { StatTile } from "@/components/molecules/StatTile/StatTile";
import { WeeklyHoursBars } from "@/components/molecules/WeeklyHoursBars/WeeklyHoursBars";
import { LatestTasks } from "@/components/organisms/LatestTasks/LatestTasks";

import { hoursDelta } from "@/utils/UAnalytics";
import { convertToTime } from "@/utils/UDates";
import { parseHours } from "@/utils/UWorkDays";

import { TPeriodStats, TProject, TWeekStats } from "@/types";

const WEEKS = 8;

interface IProjectOverviewTabProps {
	project: TProject;
}

const euros = (amount: number) =>
	new Intl.NumberFormat("fr-FR", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 0,
	}).format(amount);

/**
 * Worked hours ÷ the person's daily hours × the project's daily rate:
 * what the month is worth. The rate faces the client (the project's
 * override, else the client's default) and the day is the person's
 * (docs/modele.md, phases 2 and 4).
 */
export const billableOf = (
	stats: Pick<TPeriodStats, "workedHours"> | undefined,
	hoursPerDay: string | null | undefined,
	rate: number | null | undefined
): number | null => {
	const day = parseHours(hoursPerDay ?? undefined);
	if (!stats || !rate || rate <= 0 || day <= 0) return null;
	return (stats.workedHours.raw / day) * rate;
};

/** The server's weeks as the bars want them: a short label, minutes, the running one last. */
export const barsOf = (weeks: TWeekStats[]) =>
	weeks.map((week, index) => ({
		label: `S${getISOWeek(new Date(week.weekStart))}`,
		minutes: Math.round(week.workedHours.raw * 60),
		isCurrent: index === weeks.length - 1,
	}));

/**
 * The "Aperçu" tab: this month's figures, the last eight weeks as
 * bars, the latest tasks (LatestTasks), the project's details and who
 * works on it. The figures come in one answer
 * (GET /projects/:id/overview): the month, the one before, the total
 * since creation, the worked days and the weeks.
 */
export const ProjectOverviewTab: FC<IProjectOverviewTabProps> = ({
	project,
}) => {
	const overview = useProjectOverview(project.id);
	const { settings } = useUserSettings();
	const figures = overview.data;
	const sharingOn = useFeatureFlag("sharing");
	// Whoever works on this project: its own shares, and whoever the
	// client or the workspace lets in - the API answers with all three.
	// Not asked while sharing is paused: the card is hidden, the API 404s.
	const members = useQuery({
		queryKey: ["projects", project.id, "members"],
		queryFn: () => MembersService.list("projects", project.id),
		enabled: sharingOn,
	});

	const weeks = useMemo(() => barsOf(figures?.weeks ?? []), [figures?.weeks]);
	const workedDays = figures?.workedDays ?? 0;
	const avgPerDay =
		figures && workedDays > 0
			? figures.period.workedHours.raw / workedDays
			: null;
	// The project's own rate; without one the client's default applies,
	// which only the API knows - the tile then says so.
	const rate = project.rate ?? null;
	const billable = billableOf(figures?.period, settings?.hoursPerDay, rate);

	return (
		<Grid columns={2} minItemWidth="400px" gap="1.5rem">
			<div className="flex flex-col gap-6">
				<Grid
					columns={3}
					minItemWidth="180px"
					gap="1rem"
					data-testid="project-stats"
				>
					<StatTile
						label={t("projects.stats.hoursMonth")}
						value={figures?.period.workedHours.str ?? "—"}
						delta={hoursDelta(figures?.period, figures?.previous)}
						sub={t("views.stats.vsPreviousMonth")}
						isLoading={overview.isLoading}
					/>
					<StatTile
						label={t("projects.stats.avgPerDay")}
						value={
							avgPerDay !== null ? convertToTime(avgPerDay) : "—"
						}
						sub={t("projects.stats.days", { count: workedDays })}
						isLoading={overview.isLoading}
					/>
					<StatTile
						label={t("projects.stats.billable")}
						value={billable !== null ? euros(billable) : "—"}
						sub={
							billable !== null
								? `${t("projects.rate")} ${rate} €`
								: t("projects.stats.noRate")
						}
						isLoading={overview.isLoading}
					/>
				</Grid>

				<SectionCard
					title={t("projects.weeklyHours")}
					actions={
						<span className="text-xs text-neutral-500">
							{t("projects.lastWeeks", { count: WEEKS })}
						</span>
					}
				>
					{overview.isLoading ? (
						<Spinner className="mx-auto my-8" />
					) : (
						<WeeklyHoursBars weeks={weeks} />
					)}
				</SectionCard>

				<LatestTasks project={project} limit={5} />
			</div>

			<div className="flex flex-col gap-6">
				<SectionCard title={t("projects.infos.title")}>
					<SummaryList
						items={[
							{
								label: t("client.label"),
								value: project.client?.canOpen ? (
									<Link
										to={`/clients/${project.client.id}`}
										className="inline-flex items-center gap-1.5"
									>
										<Dot
											color={project.client.color}
											className="h-2 w-2 min-w-2"
										/>
										{project.client.name}
									</Link>
								) : project.client ? (
									// Given the project only: the client's name, not its page.
									<span className="inline-flex items-center gap-1.5">
										<Dot
											color={project.client.color}
											className="h-2 w-2 min-w-2"
										/>
										{project.client.name}
									</span>
								) : (
									t("projects.client.internalShort")
								),
							},
							{
								label: t("projects.infos.createDate"),
								value: format(
									new Date(project.createDate),
									"d MMM yyyy"
								),
							},
							{
								label: t("projects.infos.totalHours"),
								value: figures?.total.workedHours.str ?? "—",
							},
							{
								label: t("projects.infos.status"),
								value: (
									<Badge
										size="sm"
										variant="tint"
										color={
											project.isArchived
												? "neutral"
												: "success"
										}
									>
										{project.isArchived
											? t("projects.infos.archived")
											: t("projects.infos.active")}
									</Badge>
								),
							},
						]}
					/>
				</SectionCard>

				<FeatureGate feature="sharing">
					<SectionCard title={t("projects.infos.members")}>
						<ul className="flex flex-col divide-y divide-neutral-100">
							{(members.data ?? []).map((member) => (
								<li
									key={member.userId}
									className="flex items-center gap-3 py-2 text-sm"
								>
									<MemberAvatar
										member={member}
										className="h-7 w-7"
									/>
									<span className="grow truncate">
										{member.firstName} {member.lastName}
									</span>
									<span className="text-xs text-neutral-500">
										{t(`roles.${member.role}`)}
									</span>
								</li>
							))}
						</ul>
					</SectionCard>
				</FeatureGate>
			</div>
		</Grid>
	);
};
